import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, AlertCircle, AlertTriangle, Info, BellOff } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { AlertCard, type AlertData } from './AlertCard';
import { AcknowledgeModal } from './AcknowledgeModal';

type SeverityFilter = 'all' | 'critical' | 'warning' | 'info';
type TypeFilter = 'all' | 'water_quality' | 'performance';

interface AlertsResponse {
  data: {
    alerts: AlertData[];
    total: number;
    has_more: boolean;
  };
}

interface ActiveAlertsProps {
  farmId: string;
}

const typeFilters: Record<string, string[]> = {
  water_quality: ['low_do', 'high_do', 'high_ph', 'low_ph', 'high_ammonia', 'high_nitrite', 'low_temp', 'high_temp', 'low_salinity', 'high_salinity', 'ph_dial_high'],
  performance: ['low_growth', 'low_survival', 'high_fcr'],
};

export function ActiveAlerts({ farmId }: ActiveAlertsProps) {
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [selectedAlert, setSelectedAlert] = useState<AlertData | null>(null);

  const queryClient = useQueryClient();

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['alerts', farmId, 'active'],
    queryFn: () => api.get<AlertsResponse>(`/api/v1/farms/${farmId}/alerts`, {
      params: { status: 'active' }
    }),
    refetchInterval: 30000,
  });

  const acknowledgeMutation = useMutation({
    mutationFn: ({ alertId, notes }: { alertId: string; notes?: string }) =>
      api.post(`/api/v1/farms/${farmId}/alerts/${alertId}/acknowledge`, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts', farmId] });
      setSelectedAlert(null);
    },
  });

  const alerts = data?.data?.alerts || [];

  // Apply filters
  const filteredAlerts = alerts.filter((alert) => {
    if (severityFilter !== 'all' && alert.severity !== severityFilter) return false;
    if (typeFilter !== 'all' && !typeFilters[typeFilter]?.includes(alert.alert_type)) return false;
    return true;
  });

  // Group by severity
  const criticalAlerts = filteredAlerts.filter((a) => a.severity === 'critical');
  const warningAlerts = filteredAlerts.filter((a) => a.severity === 'warning');
  const infoAlerts = filteredAlerts.filter((a) => a.severity === 'info');

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <LoadingSpinner size={32} />
        <p className="text-sm text-gray-500">Loading alerts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="w-full sm:w-48">
          <Select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}
            options={[
              { value: 'all', label: 'All Severities' },
              { value: 'critical', label: 'Critical Only' },
              { value: 'warning', label: 'Warning Only' },
              { value: 'info', label: 'Info Only' },
            ]}
          />
        </div>
        <div className="w-full sm:w-48">
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
            options={[
              { value: 'all', label: 'All Types' },
              { value: 'water_quality', label: 'Water Quality' },
              { value: 'performance', label: 'Performance' },
            ]}
          />
        </div>
        <div className="flex-1" />
        <Button
          variant="outline"
          onClick={() => refetch()}
          loading={isRefetching}
          leftIcon={<RefreshCw className="w-4 h-4" />}
          className="sm:w-auto"
        >
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <SeverityCountCard
          severity="critical"
          count={criticalAlerts.length}
          icon={AlertCircle}
        />
        <SeverityCountCard
          severity="warning"
          count={warningAlerts.length}
          icon={AlertTriangle}
        />
        <SeverityCountCard
          severity="info"
          count={infoAlerts.length}
          icon={Info}
        />
      </div>

      {/* Alert List */}
      {filteredAlerts.length === 0 ? (
        <EmptyState hasFilters={severityFilter !== 'all' || typeFilter !== 'all'} />
      ) : (
        <div className="space-y-3">
          {/* Critical alerts first */}
          {criticalAlerts.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onAcknowledge={() => setSelectedAlert(alert)}
            />
          ))}
          {/* Then warnings */}
          {warningAlerts.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onAcknowledge={() => setSelectedAlert(alert)}
            />
          ))}
          {/* Finally info */}
          {infoAlerts.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onAcknowledge={() => setSelectedAlert(alert)}
            />
          ))}
        </div>
      )}

      {/* Acknowledge Modal */}
      {selectedAlert && (
        <AcknowledgeModal
          alert={selectedAlert}
          open={!!selectedAlert}
          onClose={() => setSelectedAlert(null)}
          onConfirm={(notes) =>
            acknowledgeMutation.mutate({ alertId: selectedAlert.id, notes })
          }
          isLoading={acknowledgeMutation.isPending}
        />
      )}
    </div>
  );
}

interface SeverityCountCardProps {
  severity: 'critical' | 'warning' | 'info';
  count: number;
  icon: typeof AlertCircle;
}

function SeverityCountCard({ severity, count, icon: Icon }: SeverityCountCardProps) {
  const config = {
    critical: {
      bg: 'bg-gradient-to-br from-red-50 to-red-100/50',
      border: 'border-red-200/60',
      text: 'text-red-700',
      iconColor: 'text-red-500',
      countColor: 'text-red-600',
    },
    warning: {
      bg: 'bg-gradient-to-br from-amber-50 to-amber-100/50',
      border: 'border-amber-200/60',
      text: 'text-amber-700',
      iconColor: 'text-amber-500',
      countColor: 'text-amber-600',
    },
    info: {
      bg: 'bg-gradient-to-br from-blue-50 to-blue-100/50',
      border: 'border-blue-200/60',
      text: 'text-blue-700',
      iconColor: 'text-blue-500',
      countColor: 'text-blue-600',
    },
  }[severity];

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl border p-4',
        'transition-all duration-200',
        config.bg,
        config.border,
        count > 0 && 'ring-1 ring-inset',
        count > 0 && severity === 'critical' && 'ring-red-200',
        count > 0 && severity === 'warning' && 'ring-amber-200',
        count > 0 && severity === 'info' && 'ring-blue-200'
      )}
    >
      <div className="flex items-center gap-3">
        <Icon className={cn('w-5 h-5', config.iconColor)} />
        <div>
          <p className={cn('text-2xl font-bold tabular-nums', config.countColor)}>
            {count}
          </p>
          <p className={cn('text-xs font-medium capitalize', config.text)}>
            {severity}
          </p>
        </div>
      </div>
    </div>
  );
}

interface EmptyStateProps {
  hasFilters: boolean;
}

function EmptyState({ hasFilters }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <BellOff className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">
        {hasFilters ? 'No matching alerts' : 'All clear!'}
      </h3>
      <p className="text-sm text-gray-500 text-center max-w-sm">
        {hasFilters
          ? 'Try adjusting your filters to see more alerts.'
          : 'All water quality parameters are within normal ranges. Great job!'}
      </p>
    </div>
  );
}
